import get from "lodash/get";
import PT from "prop-types";
import React, { Fragment, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { TAG_PAGE_URL_PREFIX } from "../../constants";
import { HeroImage } from "../atoms/HeroImage";
import { Label } from "../atoms/Label";
import Newsletter from "../atoms/Newsletter";
import { SvgIconHandler } from "../atoms/svg-icon-hadler";
import { useDeviceType } from "../custom-hooks/useDeviceType";
import { getStoryTemplate, storyTemplates } from "../utils/story";
import { DfpComponent } from "../ads/dfp-component";
import { Link } from "@quintype/components";

import {
  AuthorBox,
  AuthorDetails,
  BreadCrumbs,
  FollowUs,
  Headline,
  MultipleAuthorDetails,
  PublishDetails,
  ReadTime,
  SocialShareIcons,
  SubHeadline,
} from "./atoms";
import { GooglePs } from "./atoms/GooglePs";
import AdWrapper from "./atoms/AdWrapper";
import StarRating from "./atoms/StarRatings";
import CustomStoryElement from "./custom-story-elements";
import { DefaultStoryCard } from "./DefaultStoryCard";

import { getQueryParamsForArticleApi } from "../utils/utils";
import "./text.m.css";

Modal.setAppElement("body");

function StoryCard(props) {
  const attribute = get(props, ["card", "metadata", "attributes", "cardtype", "0"], null);
  const {
    cardsWithTitleRef,
    isListicleTitleNumbering123,
    isListicleTitleNumbering321,
    isListicleTitleNumberingBullets,
    isListicle,
    isStoryListicle,
    totalCards,
    count,
    card,
    story,
    allImagesInCards,
    counter,
  } = props;

  const shouldRenderTitle =
    !attribute &&
    !cardsWithTitleRef?.includes(count + 1) &&
    ((isListicleTitleNumbering123 && isListicle) || isStoryListicle);

  const isTitlePresent = !attribute && !cardsWithTitleRef?.includes(count + 1) && isListicle;

  let titleContent = null;

  if (isListicleTitleNumbering321) {
    titleContent = `${totalCards - count}\u002E`;
  }
  if (isListicleTitleNumberingBullets) {
    titleContent = "\u00B7";
  }

  return (
    <Fragment>
      {shouldRenderTitle && <h2 styleName="title">{`${count + 1}\u002E`}</h2>}
      {isTitlePresent && titleContent && <h2 styleName="title">{titleContent}</h2>}

      {attribute ? (
        <CustomStoryElement card={card} attribute={attribute} story={story} counter={counter} />
      ) : (
        <DefaultStoryCard card={card} story={story} allImagesInCards={allImagesInCards} counter={counter} />
      )}
    </Fragment>
  );
}

StoryCard.propTypes = {
  cardsWithTitleRef: PT.array,
  isListicleTitleNumbering123: PT.bool,
  isListicleTitleNumbering321: PT.bool,
  isListicleTitleNumberingBullets: PT.bool,
  isStoryListicle: PT.bool,
  isListicle: PT.bool,
  totalCards: PT.number,
  card: PT.object,
  story: PT.object,
  counter: PT.object,
  count: PT.number || PT.undefined,
  allImagesInCards: PT.object,
};

const ReviewStoryComponent = ({ story }) => {
  return (
    <div styleName="review-container">
      {story?.metadata?.["review-title"] && <span styleName="review-title">{story?.metadata?.["review-title"]}</span>}
      {story?.metadata?.director && <span styleName="review-content">Director: {story?.metadata?.director}</span>}
      {story?.metadata?.cast && <span styleName="review-content">Cast: {story?.metadata?.cast}</span>}
      {story?.metadata?.["review-rating"]?.value && <StarRating story={story} />}
    </div>
  );
};

const CardsWithAds = ({ story }) => {
  const counter = { ads: 0, paragraphs: 0 };
  const cardsWithTitleRef = useRef([]);
  const allImagesInCards = {};
  let count = 0;
  story?.cards?.map((card) => {
    card?.["story-elements"]?.map((element) => {
      if (element?.type === "image" && !card?.metadata?.attributes?.cardtype?.length) {
        allImagesInCards[element?.id] = ++count;
      }
    });
  });

  const isListicle = getStoryTemplate(story) === storyTemplates.listicle;
  const isStoryListicle = getStoryTemplate(story) === storyTemplates.storyListicle;

  function checkListicleTitleNumbering(story) {
    const bulletType = story?.["bullet-type"];
    return {
      isListicleTitleNumbering123: bulletType === "123",
      isListicleTitleNumbering321: bulletType === "321",
      isListicleTitleNumberingBullets: bulletType === "bullets",
    };
  }

  const { isListicleTitleNumbering123, isListicleTitleNumbering321, isListicleTitleNumberingBullets } =
    checkListicleTitleNumbering(story);

  function updateElementText(element, newTitle, condition, prefix) {
    if (condition && !element?.text?.startsWith(prefix)) {
      element.text = newTitle;
    }
  }

  function formatTitle(count, totalCards, element, isListicle, isStoryListicle) {
    let listiclePrefix = "";

    if (isListicle) {
      if (isListicleTitleNumberingBullets) {
        listiclePrefix = `\u00B7 `;
      }

      if (isListicleTitleNumbering321) {
        listiclePrefix = `${totalCards - count}\u002E `;
      }

      if (!isListicleTitleNumberingBullets && !isListicleTitleNumbering321) {
        listiclePrefix = `${count + 1}\u002E `;
      }
    }

    const storyListiclePrefix = isStoryListicle ? `${count + 1}\u002E ` : "";

    const titlePrefix = listiclePrefix || storyListiclePrefix;

    return titlePrefix + (element?.text || "");
  }

  const moveFirstYouTubeVideoToFirstCard = (cards) => {
    if (cards.length === 0) {
      return;
    }
    let firstYouTubeVideo = null;
    let videoIndex = null;
    let storyElementIndex = null;

    cards.forEach((data, i) => {
      if (!firstYouTubeVideo && data["story-elements"]) {
        data["story-elements"].forEach((element, j) => {
          if (
            (element.type === "youtube-video" ||
              element.subtype === "dailymotion-video" ||
              element.subtype === "dailymotion-embed-script") &&
            !firstYouTubeVideo
          ) {
            firstYouTubeVideo = element;
            videoIndex = i;
            storyElementIndex = j;
          }
        });
      }
    });

    if (firstYouTubeVideo) {
      if (cards[0] && cards[0]["story-elements"]) {
        cards[0]["story-elements"].unshift(firstYouTubeVideo);
      }

      cards[videoIndex]["story-elements"].splice(videoIndex !== 0 ? storyElementIndex : storyElementIndex + 1, 1);
    }

    return cards;
  };

  const prependCountToTitleForListicle = (cardData, count, totalCards) => {
    let firstTitleProcessed = false;
    const numberingConditions = [
      { condition: isListicleTitleNumbering123, prefix: (count + 1).toString() + "\u002E" },
      { condition: isListicleTitleNumbering321, prefix: (totalCards - count).toString() + "\u002E" },
      { condition: isListicleTitleNumberingBullets, prefix: "\u00B7" },
    ];

    const updatedStoryElements = cardData["story-elements"].map((element) => {
      let newTitle = "";

      if (!firstTitleProcessed && element?.type === "title" && (isListicle || isStoryListicle)) {
        newTitle = formatTitle(count, totalCards, element, isListicle, isStoryListicle);

        if (isListicle) {
          numberingConditions.forEach(({ condition, prefix }) => {
            updateElementText(element, newTitle, condition, prefix);
          });
        } else {
          updateElementText(element, newTitle, true, (count + 1).toString());
        }

        cardsWithTitleRef.current = [...cardsWithTitleRef.current, count + 1];
        firstTitleProcessed = true;
      }

      return element;
    });

    return { ...cardData, "story-elements": updatedStoryElements };
  };

  const cardsWithoutAttributes = {};
  let cardCount = 0;
  story?.cards?.map((card) => {
    const attribute = get(card, ["metadata", "attributes", "cardtype", "0"], null);
    if (!attribute) {
      cardsWithoutAttributes[card?.id] = cardCount++;
    }
  });

  return (story?.["story-template"] === "video" ? moveFirstYouTubeVideoToFirstCard(story?.cards) : story?.cards)?.map(
    (card) => {
      const totalCards = Object.keys(cardsWithoutAttributes)?.length;
      const count = cardsWithoutAttributes[card?.id];
      let updatedCard;
      if (count !== undefined) {
        updatedCard = prependCountToTitleForListicle(card, count, totalCards);
      } else {
        updatedCard = card;
      }

      return (
        <StoryCard
          key={updatedCard?.id}
          card={updatedCard}
          story={story}
          cardsWithTitleRef={cardsWithTitleRef.current}
          isListicleTitleNumbering123={isListicleTitleNumbering123}
          isListicleTitleNumbering321={isListicleTitleNumbering321}
          isListicleTitleNumberingBullets={isListicleTitleNumberingBullets}
          isListicle={isListicle}
          isStoryListicle={isStoryListicle}
          count={count}
          totalCards={totalCards}
          allImagesInCards={allImagesInCards}
          counter={counter}
        />
      );
    }
  );
};

const TextStoryTemplate = ({ story, config, params, bottomAdUnit }) => {
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const timezone = useSelector((state) => get(state, ["qt", "data", "timezone"], null));
  const adsConfig = get(qtState, ["config", "ads-config"], {});
  const label = get(story, ["metadata", "story-attributes", "label", "0"], "");
  const articleApi = get(qtState, ["config", "articleApi"], false);
  const storyPageBottomAd = get(adsConfig, ["slots", "story_page_bottom_ad"], {});
  const param = getQueryParamsForArticleApi(useSelector((state) => get(state, ["qt", "currentPath"], "")));
  let isApi = false;
  if (param.articleApi) {
    isApi = true;
  }
  const apiAdUnit = "/1019334/GULFNEWS/Gulfnews_App/gulfnews_newapp_articles/gulfnews_newapp_articles_bottom";
  const template = get(story, ["story-template"], "");
  const authorBoxAttr = get(story, ["metadata", "story-attributes", "authorbox", "0"], "hide");

  const centerTopAdAtf = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "gold_forex_center_top_ad_atf"], {})
  );
  const currPath = useSelector((state) => get(state, ["qt", "currentPath"], ""))
    .split("?")[0]
    .split("/")[1];
  const pageName = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "ad_name_mapping", currPath], "sections")
  );
  const isUaeSection = pageName === "uae";
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const isMobile = deviceType === "mobile";

  return (
    <div styleName="template-wrapper">
      <div styleName="lhs-wrapper">
        <div styleName="meta-data-section">
          {label && <Label story={story} />}
          <Headline headline={story.headline} />
          <SubHeadline subheadline={story.subheadline} />
          <PublishDetails story={story} template="story" timezone={timezone} />
          <AuthorDetails story={story} config={config} />
          {isUaeSection && (
            <div styleName="ad-slot-320x50-wrapper">
              {isMobile && (
                <DfpComponent
                  adStyleName="ad-slot-size-300x250-constant"
                  id={`uae_page-top2-atf`}
                  path={"/1019334/GULFNEWS/Gulfnews_ATF/gulfnews_uae_atf/gulfnews_uae_top2_atf"}
                  size={centerTopAdAtf.sizes}
                  isSponsored={true}
                  bgColor={false}
                />
              )}
            </div>
          )}
          <div styleName="read-time-social-share-div">
            {story["read-time"] ? <ReadTime time={story["read-time"]} /> : <div styleName="border-top-mobile"></div>}
            <span styleName="follow-us-share">
              <GooglePs />
              <FollowUs />
              <SocialShareIcons
                story={story}
                imageDimension={{ width: "34px", height: "34px", viewbox: "0 0 34 34" }}
              />
            </span>
          </div>
        </div>
        <div styleName="hero-image-section">
          {template !== "video" && <HeroImage story={story} aspectRatio={[[4, 3]]} isStoryPageImage />}
        </div>
        <div styleName="content-section">
          {story?.["story-template"] === "review" && story?.metadata?.["review-rating"] && (
            <ReviewStoryComponent story={story} />
          )}
          <CardsWithAds story={story} />
          {authorBoxAttr.toLowerCase() === "show" && <AuthorBox story={story} />}
          {story?.authors?.length > 3 && (
            <div styleName="multiple-authors">
              <MultipleAuthorDetails story={story} />
            </div>
          )}
          {story.tags?.length > 0 && (
            <div styleName="tags">
              <div styleName="tag-title">Related Topics:</div>
              <div styleName="tags-list">
                {story.tags?.map((tag) => (
                  <span key={tag.id}>
                    <Link href={`${TAG_PAGE_URL_PREFIX}${tag.slug}`}>{tag.name}</Link>
                  </span>
                ))}
              </div>
            </div>
          )}
          {!isDesktop && <div id="below-article"></div>}
          {!articleApi && <Newsletter story={story} />}

          {isDesktop && (
            <AdWrapper
              adStyleName="story-page-ad-slot"
              id={`${template}-${story?.id}-bottom-ad`}
              path={isApi ? apiAdUnit : bottomAdUnit}
              size={storyPageBottomAd.sizes}
              viewPortSizeMapping={storyPageBottomAd.view_port_size_mapping}
              isLhsAd={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

TextStoryTemplate.propTypes = {
  card: PT.object,
  story: PT.object,
  params: PT.object,
};

function Text({ story, config, params = {} }) {
  const qtState = useSelector((state) => get(state, ["qt"], {}));

  const template = get(story, ["story-template"], "");
  const adsConfig = get(qtState, ["config", "ads-config"], {});
  const articleApi = get(qtState, ["config", "articleApi"], false);
  const storyPageBottomAd = get(adsConfig, ["slots", "story_page_bottom_ad"], {});
  const disableUpNext = get(params, ["disableUpNext"], false);
  const currPath = useSelector((state) => get(state, ["qt", "currentPath"], ""))
    .split("?")[0]
    .split("/")[1];
  const pageName = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "ad_name_mapping", currPath], "sections")
  );
  const networkId = useSelector((state) => get(state, ["qt", "config", "ads-config", "network_id"], ""));
  const bottomAdUnit = `/${networkId}/GULFNEWS/Gulfnews_TextAd/gulfnews_${pageName}_textad/gulfnews_${pageName}_textad_article_bottom`;
  const noAds = get(qtState, ["config", "noAds"], false);

  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";

  return (
    <>
      <div className="story-grid" styleName="story-grid-container">
        {!articleApi && <BreadCrumbs currentPath={story?.slug} />}
        <TextStoryTemplate story={story} config={config} params={params} bottomAdUnit={bottomAdUnit} />
        {/* BOTTOM AD */}

        {(isMobile || isTablet) && bottomAdUnit && !noAds && (
          <AdWrapper
            adStyleName="story-page-ad-bottom-slot"
            id={`${template}-${story?.id}-lhs-ad`}
            path={bottomAdUnit}
            size={storyPageBottomAd.sizes}
            viewPortSizeMapping={storyPageBottomAd.view_port_size_mapping}
            isLhsAd={true}
          />
        )}
        {isDesktop && <div id="below-article"></div>}
        {!disableUpNext && (
          <div styleName="up-next-wrapper">
            <div styleName="up-next-title">
              <span>Up Next</span>
              <SvgIconHandler type="point-arrow" width="24px" height="24px" />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

Text.propTypes = {
  story: PT.object,
  config: PT.object,
};

export { Text };
